/* Brelir · comportamiento propio sobre Dawn 16 (sin dependencias). */

(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- Pestañas por superficie ---------- */
  /** Pestañas accesibles: clic, flechas, Inicio y Fin. */
  class BrelirTabs extends HTMLElement {
    #abort = null;
    #tabs = [];
    #panels = [];

    connectedCallback() {
      this.#tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.#panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
      if (!this.#tabs.length) return;

      this.#abort = new AbortController();
      const { signal } = this.#abort;
      for (const [index, tab] of this.#tabs.entries()) {
        tab.addEventListener('click', () => this.select(index, false), { signal });
        tab.addEventListener('keydown', (event) => this.#onKeydown(event, index), { signal });
      }

      const current = this.#tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
      this.select(current > -1 ? current : 0, false);
    }

    disconnectedCallback() {
      this.#abort?.abort();
    }

    /**
     * @param {KeyboardEvent} event
     * @param {number} index
     */
    #onKeydown(event, index) {
      const last = this.#tabs.length - 1;
      const keys = { ArrowRight: index === last ? 0 : index + 1, ArrowLeft: index === 0 ? last : index - 1, Home: 0, End: last };
      if (!(event.key in keys)) return;
      event.preventDefault();
      this.select(keys[event.key], true);
    }

    /**
     * Enseña una pestaña y esconde las demás.
     * @param {number} index - Pestaña que se enseña
     * @param {boolean} focus - Mover el foco a la pestaña
     */
    select(index, focus) {
      for (const [i, tab] of this.#tabs.entries()) {
        const selected = i === index;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        if (selected && focus) tab.focus();
      }
      // Los vídeos de cada pestaña los arranca y para <brelir-video> según se vean o no.
      for (const [i, panel] of this.#panels.entries()) panel.hidden = i !== index;
    }
  }

  if (!customElements.get('brelir-tabs')) customElements.define('brelir-tabs', BrelirTabs);

  /* ---------- Vídeo corto: solo, sin sonido, en bucle y solo mientras se ve ---------- */
  class BrelirVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.button = this.querySelector('.brelir-video__toggle');
      if (!this.video || this.ready) return;
      this.ready = true;

      const video = this.video;
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.loop = true;
      video.controls = false;
      video.setAttribute('playsinline', '');
      if (this.dataset.label) {
        video.setAttribute('aria-label', this.dataset.label);
      } else {
        video.setAttribute('aria-hidden', 'true');
      }

      // Con «reducir movimiento» no arranca solo: la persona decide con el botón.
      this.userPaused = reduceMotion.matches;
      this.visible = false;
      this.selfPausing = false;

      if (this.button) {
        this.button.hidden = false;
        this.button.addEventListener('click', () => this.toggle());
      }
      video.addEventListener('play', () => this.syncButton());
      video.addEventListener('pause', () => {
        this.syncButton();
        // Dawn para todos los vídeos al cambiar de foto en la galería: si este se sigue viendo, vuelve a arrancar.
        if (!this.selfPausing && !this.userPaused && this.visible) {
          window.setTimeout(() => this.update(), 400);
        }
        this.selfPausing = false;
      });

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            this.visible = entries.some((entry) => entry.isIntersecting);
            this.update();
          },
          { threshold: 0.35 }
        );
        this.observer.observe(this);
      } else {
        this.visible = true;
        this.update();
      }
      this.syncButton();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.ready = false;
    }

    update() {
      if (this.visible && !this.userPaused) {
        if (this.video.paused) this.play();
      } else if (!this.video.paused) {
        this.selfPausing = true;
        this.video.pause();
      }
    }

    play() {
      if (this.video.preload === 'none') this.video.preload = 'metadata';
      const attempt = this.video.play();
      // Si el navegador no deja arrancar solo (por ejemplo, en ahorro de batería), queda el botón.
      if (attempt && typeof attempt.catch === 'function') attempt.catch(() => this.syncButton());
    }

    toggle() {
      if (this.video.paused) {
        this.userPaused = false;
        this.play();
      } else {
        this.userPaused = true;
        this.selfPausing = true;
        this.video.pause();
      }
    }

    syncButton() {
      const playing = !this.video.paused;
      this.classList.toggle('is-playing', playing);
      if (!this.button) return;
      // Los textos vienen de locales (brelir.video) en data-label-pause y data-label-play.
      const label = playing ? this.dataset.labelPause : this.dataset.labelPlay;
      if (label) this.button.setAttribute('aria-label', label);
    }
  }

  if (!customElements.get('brelir-video')) customElements.define('brelir-video', BrelirVideo);

  /* ---------- Calculadora de ahorro (sections/brelir-calculator.liquid) ---------- */
  /** Calculadora de gasto en desechables frente al pack (sections/brelir-calculator.liquid). */
  class BrelirCalculator extends HTMLElement {
    #abort = null;

    connectedCallback() {
      this.spend = this.querySelector('[data-calc-spend]');
      this.spendText = this.querySelector('[data-calc-spend-text]');
      this.weeks = this.querySelector('[data-calc-weeks]');
      this.#abort = new AbortController();
      for (const input of this.querySelectorAll('.brelir-calc__input')) {
        input.addEventListener('change', () => this.update(), { signal: this.#abort.signal });
      }
      this.update();
    }

    disconnectedCallback() {
      this.#abort?.abort();
    }

    /**
     * @param {number} value
     * @returns {string} Importe redondeado con el formato del país
     */
    money(value) {
      const locale = this.dataset.locale || 'es-MX';
      return `${this.dataset.prefix || ''}${Math.round(value).toLocaleString(locale)}`;
    }

    /** Vuelve a hacer la cuenta con los tapetes al día elegidos. */
    update() {
      const checked = this.querySelector('.brelir-calc__input:checked');
      const perDay = Number(checked ? checked.value : 1);
      const low = Number(this.dataset.low);
      const high = Number(this.dataset.high);
      const pack = Number(this.dataset.pack);
      const days = Number(this.dataset.days);
      if (!perDay || !low || !high || !pack || !days) return;

      if (this.spend) {
        this.spend.textContent = `${this.money(low * perDay * days)} – ${this.money(high * perDay * days)}${this.dataset.suffix || ''}`;
      }
      if (this.spendText) this.spendText.textContent = (this.dataset.textSpend || '').replace('[n]', perDay);

      // Semanas en las que los desechables ya cuestan lo mismo que el pack: antes con los caros, después con los baratos.
      const weeks = (price) => String(Math.max(1, Math.round(pack / (perDay * price * 7))));
      if (this.weeks) {
        this.weeks.textContent = (this.dataset.textWeeks || '')
          .replace('[desde]', weeks(high))
          .replace('[hasta]', weeks(low))
          .replace('[from]', weeks(high))
          .replace('[to]', weeks(low));
      }
    }
  }

  if (!customElements.get('brelir-calculator')) customElements.define('brelir-calculator', BrelirCalculator);

  /* ---------- Barra fija de compra ---------- */
  class BrelirStickyBuy extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-sticky-add]');
      this.priceTarget = this.querySelector('[data-sticky-price]');
      this.amountTarget = this.querySelector('[data-sticky-amount]');
      this.variantTarget = this.querySelector('[data-sticky-variant]');

      // Se decide con la posición real del botón principal en cada fotograma de scroll: así también
      // aparece si la página salta de golpe (deslizar rápido en el móvil, enlaces a una sección o volver atrás).
      this.onScroll = () => {
        if (this.ticking) return;
        this.ticking = true;
        window.requestAnimationFrame(() => {
          this.ticking = false;
          this.updateVisibility();
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });

      this.button?.addEventListener('click', () => this.addToCart());
      this.watchMainButton();
      this.syncFromPage();
      this.updateVisibility();

      // Deja hueco al final de la página para que la barra no tape el pie.
      document.body.classList.add('brelir-has-sticky-buy');
      const setHeight = () =>
        document.documentElement.style.setProperty('--brelir-sticky-buy-height', `${this.offsetHeight}px`);
      setHeight();
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(setHeight);
        this.resizeObserver.observe(this);
      }

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, () => {
          // Dawn ya ha repintado la ficha: volvemos a leer precio, variante y botón.
          window.requestAnimationFrame(() => {
            this.watchMainButton();
            this.syncFromPage();
          });
        });
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      this.resizeObserver?.disconnect();
      if (this.unsubscribe) this.unsubscribe();
      document.body.classList.remove('brelir-has-sticky-buy');
    }

    get mainSection() {
      return document.querySelector('product-info[id^="MainProduct-"]') || document.querySelector('product-info');
    }

    watchMainButton() {
      // Dawn repinta la ficha al cambiar de pack: el botón puede ser otro elemento.
      this.mainButton = this.mainSection?.querySelector('.product-form__submit') || null;
      this.updateVisibility();
    }

    updateVisibility() {
      if (!this.mainButton || !this.mainButton.isConnected) {
        this.mainButton = this.mainSection?.querySelector('.product-form__submit') || null;
      }
      if (!this.mainButton) {
        this.removeAttribute('data-visible');
        this.inert = true;
        return;
      }
      // Visible solo cuando el botón principal ya ha quedado por encima de la pantalla.
      // Escondida, la barra no recibe el foco ni la leen los lectores de pantalla (inert).
      const scrolledPast = this.mainButton.getBoundingClientRect().bottom < 0;
      this.toggleAttribute('data-visible', scrolledPast);
      this.inert = !scrolledPast;
    }

    syncFromPage() {
      const section = this.mainSection;
      if (!section) return;

      const price = section.querySelector('[id^="price-"] .price');
      if (price && this.priceTarget) this.priceTarget.innerHTML = price.outerHTML;

      // Importe que se paga ahora (el de oferta si lo hay), en texto, para la línea del pack en móvil.
      if (this.amountTarget) {
        const current = price?.querySelector(
          price.classList.contains('price--on-sale') ? '.price__sale .price-item--sale' : '.price__regular .price-item--regular'
        );
        this.amountTarget.textContent = current ? current.textContent.trim() : '';
      }

      // Lo elegido, corto: con packs en tarjetas, la talla abreviada y el pack («S · Buy 2, Get 1 Free»),
      // que es lo que explica el precio; sin packs, la primera opción, como antes.
      const checked = section.querySelector('variant-selects fieldset input:checked');
      const pack = section.querySelector('variant-selects .brelir-packs input:checked');
      let variantText = checked ? checked.value : '';
      if (pack) {
        const size = checked && checked !== pack ? checked.value.split(' · ')[0] : '';
        variantText = size ? `${size} · ${pack.value}` : pack.value;
      }
      if (this.variantTarget) this.variantTarget.textContent = variantText;

      const mainButton = section.querySelector('.product-form__submit');
      if (mainButton && this.button) {
        this.button.disabled = mainButton.disabled;
        const label = mainButton.querySelector('span:not(.loading__spinner)');
        if (label && label.textContent.trim()) this.button.textContent = label.textContent.trim();
      }
    }

    addToCart() {
      const form = this.mainSection?.querySelector('product-form form');
      if (!form) return;
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        form.querySelector('[type="submit"]')?.click();
      }
    }
  }

  if (!customElements.get('brelir-sticky-buy')) customElements.define('brelir-sticky-buy', BrelirStickyBuy);

  /* ---------- Botón fijo abajo (portada y advertorial): baja a los packs ---------- */
  class BrelirStickyCta extends HTMLElement {
    connectedCallback() {
      this.onScroll = () => {
        if (this.ticking) return;
        this.ticking = true;
        window.requestAnimationFrame(() => {
          this.ticking = false;
          this.updateVisibility();
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });

      // Deja hueco al final de la página para que la barra no tape el pie.
      document.body.classList.add('brelir-has-sticky-buy');
      const setHeight = () =>
        document.documentElement.style.setProperty('--brelir-sticky-buy-height', `${this.offsetHeight}px`);
      setHeight();
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(setHeight);
        this.resizeObserver.observe(this);
      }
      this.updateVisibility();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      this.resizeObserver?.disconnect();
      document.body.classList.remove('brelir-has-sticky-buy');
    }

    updateVisibility() {
      // Sale al pasar la primera pantalla y se esconde mientras los packs se ven.
      const pastFirstScreen = window.scrollY > window.innerHeight * 0.6;
      const packs = document.querySelector('product-info');
      let packsInView = false;
      if (packs) {
        const rect = packs.getBoundingClientRect();
        packsInView = rect.top < window.innerHeight && rect.bottom > 0;
      }
      const show = pastFirstScreen && !packsInView;
      this.toggleAttribute('data-visible', show);
      // Escondida, no recibe el foco ni la leen los lectores de pantalla.
      this.inert = !show;
    }
  }

  if (!customElements.get('brelir-sticky-cta')) customElements.define('brelir-sticky-cta', BrelirStickyCta);

  /* ---------- Galería: al cambiar de color, solo las fotos de ese color ----------
     El servidor marca con «hidden» las fotos de otros colores (snippets/product-media-gallery.liquid).
     Dawn reordena la galería pero no toca esas clases: las copiamos de la respuesta nueva. */
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data } = {}) => {
      const html = data?.html;
      if (!html || !data.sectionId) return;
      const gallery = document.getElementById(`MediaGallery-${data.sectionId}`);
      const fresh = html.getElementById(`MediaGallery-${data.sectionId}`);
      if (!gallery || !fresh) return;

      const shown = new Set(
        [...fresh.querySelectorAll('li[data-media-id]:not(.hidden)')].map((item) => item.dataset.mediaId)
      );
      const anyHidden = fresh.querySelector('li[data-media-id].hidden') || gallery.querySelector('li[data-media-id].hidden');
      if (!anyHidden) return;

      gallery.querySelectorAll('li[data-media-id]').forEach((item) => {
        item.classList.toggle('hidden', !shown.has(item.dataset.mediaId));
      });
      gallery.querySelectorAll('li[data-target]').forEach((item) => {
        item.classList.toggle('hidden', !shown.has(item.dataset.target));
      });
      gallery.elements?.viewer?.resetPages?.();
      gallery.elements?.thumbnails?.resetPages?.();
    });
  }

  /* ---------- Botones que llevan al bloque de compra ---------- */
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-brelir-scroll-to-buy]');
    if (!trigger) return;
    const target = document.querySelector('product-info .brelir-packs, product-info variant-selects, product-info product-form');
    if (!target) return; // fuera de la ficha, el enlace funciona como un enlace normal
    event.preventDefault();
    target.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'center' });
    const firstInput = target.querySelector('input:checked, input, button');
    window.setTimeout(() => firstInput?.focus({ preventScroll: true }), reduceMotion.matches ? 0 : 450);
  });

  /* ---------- Carrito: pasar «1 tapete» al pack (snippets/brelir-pack-upsell.liquid) ---------- */
  // Quita la línea y añade la variante del pack en una sola petición. Solo al pulsar el botón.
  const refreshCartBubble = async () => {
    const bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return;
    const url = new URL(window.location.pathname, window.location.origin);
    url.searchParams.set('sections', 'cart-icon-bubble');
    const response = await fetch(url);
    if (!response.ok) return;
    const sections = await response.json();
    const html = new DOMParser().parseFromString(sections['cart-icon-bubble'] || '', 'text/html');
    const source = html.querySelector('.shopify-section');
    if (source) bubble.innerHTML = source.innerHTML;
  };

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-brelir-pack-swap]');
    if (!button || button.disabled) return;
    event.preventDefault();

    const box = button.closest('[data-brelir-upsell]');
    const errorNode = box?.querySelector('.brelir-upsell__error');
    if (errorNode) errorNode.hidden = true;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const newQty = Number.parseInt(button.dataset.newQty, 10) || 1;
      const updates = { [button.dataset.lineKey]: 0, [button.dataset.newId]: newQty };
      const response = await fetch(`${window.routes?.cart_update_url || '/cart/update'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const cart = await response.json();

      // Página del carrito: se recarga (líneas y totales). Cajón: se repinta con la sección del cajón.
      const drawerItems = button.closest('cart-drawer-items');
      if (!drawerItems) {
        window.location.reload();
        return;
      }
      // El aviso de carrito cambiado hace que el cajón se repinte solo (assets/cart.js); si no está, a mano.
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'brelir-pack-swap', cartData: cart });
      } else if (typeof drawerItems.onCartUpdate === 'function') {
        await drawerItems.onCartUpdate();
      }
      await refreshCartBubble();
    } catch (error) {
      console.error('[Brelir] Cambio a pack:', error);
      button.disabled = false;
      button.removeAttribute('aria-busy');
      if (errorNode) {
        errorNode.textContent = button.dataset.error || '';
        errorNode.hidden = false;
      }
    }
  });

  /* ---------- Fecha estimada de entrega (días laborables, hora de Madrid) ---------- */
  const madridToday = () => {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/Madrid',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date());
    const get = (type) => Number(parts.find((part) => part.type === type).value);
    return new Date(Date.UTC(get('year'), get('month') - 1, get('day'), 12));
  };

  const addBusinessDays = (start, days) => {
    const date = new Date(start);
    let added = 0;
    while (added < days) {
      date.setUTCDate(date.getUTCDate() + 1);
      const weekday = date.getUTCDay();
      if (weekday !== 0 && weekday !== 6) added += 1;
    }
    return date;
  };

  const formatDay = (date, locale = 'es-ES') =>
    date.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });

  const escapeHtml = (value) =>
    value.replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);

  const renderDeliveryDates = (root = document) => {
    for (const node of root.querySelectorAll('[data-brelir-delivery]')) {
      const min = Number(node.dataset.min);
      const max = Number(node.dataset.max);
      if (!min || !max || max < min || !node.dataset.text) continue;
      const today = madridToday();
      const locale = node.dataset.locale || 'es-ES';
      const from = `<strong>${escapeHtml(formatDay(addBusinessDays(today, min), locale))}</strong>`;
      const to = `<strong>${escapeHtml(formatDay(addBusinessDays(today, max), locale))}</strong>`;
      const template = escapeHtml(node.dataset.text);
      const target = node.querySelector('[data-brelir-delivery-text]');
      if (!target) continue;
      target.innerHTML = template
        .replace('[desde]', from)
        .replace('[hasta]', to)
        .replace('[from]', from)
        .replace('[to]', to);
      node.hidden = false;
    }
  };


  renderDeliveryDates();
  document.addEventListener('shopify:section:load', (event) => renderDeliveryDates(event.target));
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, () => window.requestAnimationFrame(() => renderDeliveryDates()));
  }
})();
